<template>
	<div class="dynamic-params-dispatcher">
		<div v-for="param in mapperBaseParams" :key="param.key">
			<ParamDispatcher
				v-bind="$attrs"
				v-on="$listeners"
				:node="paramDispatcherDataProvider"
				:nodeService="service"
				:nodeAction="action"
				:param="param"
				:workflowVersion="workflowVersion"
				:hasEditPermissions="hasEditPermissions"
				:key="buildFieldKey(param)"
				@update-input-field="updateInputField"
				@update-input-field-label="updateInputFieldLabel"
			/>
		</div>
	</div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';

import { mapGetters, mapActions } from 'vuex';
import { saveWidget } from '@/store/modules/widgets/actions';
import { getWidgetFieldData } from '@/store/modules/widgets/getters';
import { getConfigurationAndInterfaceIdByFieldUUID } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { editedWorkflowVersion } from '@/store/modules/selectedWorkflow/getters';
import { getService } from '@/store/modules/apps/getters';
import { getAction } from '@/store/modules/actions/getters';

import WidgetService from '@/utils/WidgetService';
import ParamsService from '@/utils/ParamsService';
import ParamDispatcher from '@/components/BlockEditor/BlockEditorParams/ParamDispatcher';

import { configurationsValuesByFieldUUID } from '@/store/modules/configurationEditor/getters';
import { setMappingConfigurationFieldValues } from '@/store/modules/configurationEditor/actions';

export default {
	name: 'DynamicParamsDispatcher',
	props: {
		interfaceFieldUUID: { type: String },
		action: { type: Object },
		service: { type: Object },
		params: { type: Array },
		dynamicNode: { type: Object },
	},
	components: { ParamDispatcher },
	computed: {
		...mapGetters([getConfigurationAndInterfaceIdByFieldUUID.fullPath]),
		...mapGetters([getService.fullPath, getAction.fullPath, editedWorkflowVersion.fullPath]),
		...mapGetters([getWidgetFieldData.fullPath, configurationsValuesByFieldUUID.fullPath]),
		widgetField() {
			return this[getWidgetFieldData.fullPath](this.interfaceFieldUUID)?.widgetField;
		},
		widgetFieldSource() {
			return this.widgetField?.metadata?.source || {};
		},
		latestWidgetFieldSource() {
			return { ...this.widgetField?.metadata?.source, ...this.localWidgetFieldSource };
		},
		mapperBaseParams() {
			return this.params?.map((x) => ({ ...x, disable_mapped_data: true }));
		},
		configurationAndInterfaceIdByFieldUUID() {
			return this[getConfigurationAndInterfaceIdByFieldUUID.fullPath](this.interfaceFieldUUID);
		},
		configurationId() {
			return this.configurationAndInterfaceIdByFieldUUID.configurationId;
		},
		interface() {
			return this.configurationAndInterfaceIdByFieldUUID.interface;
		},
		workflowVersion() {
			return this[editedWorkflowVersion.fullPath];
		},
		hasEditPermissions() {
			return true; // if we got here we are allowed to edit these fields
		},
		credentialsConfigurationValue() {
			const { credentials_configuration } = this.widgetFieldSource;
			const sourceFieldUUID = credentials_configuration.source_field_uuid;
			return this[configurationsValuesByFieldUUID.fullPath][sourceFieldUUID];
		},
		paramDispatcherDataProvider() {
			if (this.dynamicNode) {
				return {
					...this.dynamicNode,
					credentials: this.credentialsConfigurationValue,
					overrideParamsMap: true,
				};
			}

			const { params_map, params_map_labels, credentials_configuration } = this.widgetFieldSource;
			return {
				params_map,
				params_map_labels,
				user_schema_mappings_source_field_uuid: this.interfaceFieldUUID,
				credentials_configuration,
				credentials: this.credentialsConfigurationValue,
			};
		},
	},

	methods: {
		...mapActions([saveWidget.fullPath, setMappingConfigurationFieldValues.fullPath]),
		async saveParamsMapInMapperWidgetField() {
			const currentInterface = cloneDeep(this.interface);
			await WidgetService.saveParamsMapInMapperWidgetField(
				this.latestWidgetFieldSource,
				currentInterface,
				this[editedWorkflowVersion.fullPath]?.id,
				this.interfaceFieldUUID,
				this[saveWidget.fullPath]
			);
		},
		handleParamDependencies(keyChanged) {
			this.params.forEach((param) => {
				const dependsOn = param.depends_on || [];
				if (dependsOn.includes(keyChanged)) {
					this.removeParamKeyAndLabel(param.key);
				}
			});
		},
		removeParamKeyAndLabel(paramKey) {
			// Will remove label as well
			this.updateInputField({
				map: {
					key: paramKey,
					value: '',
				},
			});
			this.updateInputFieldLabel({
				label: {
					key: paramKey,
					value: '',
				},
			});
		},

		async updateInputField(payload) {
			const { map } = payload;
			const fieldType = 'params_map';
			const fieldTypeEmitName = 'send-params-map';
			await this.updateField(map, fieldType, fieldTypeEmitName);
			if (map.key) {
				this.handleParamDependencies(map.key);
			}
		},
		async updateInputFieldLabel(payload) {
			const { label } = payload;
			const fieldType = 'params_map_labels';
			const fieldTypeEmitName = 'send-params-map-labels';
			await this.updateField(label, fieldType, fieldTypeEmitName);
		},
		async updateField(newVal, fieldType, fieldTypeEmitName) {
			if (this.dynamicNode) {
				const value = { ...this.localResponseData[fieldType], ...{ [newVal.key]: newVal.value } };
				this.localResponseData = { ...this.localResponseData, ...{ [fieldType]: value } };
				await this.saveFieldValueToStore(fieldType, value);
				this.$emit(fieldTypeEmitName, value);
			} else {
				const value = { ...this.latestWidgetFieldSource[fieldType], ...{ [newVal.key]: newVal.value } };
				this.localWidgetFieldSource = { ...this.latestWidgetFieldSource, ...{ [fieldType]: value } };
				await this.saveParamsMapInMapperWidgetField();
				this.$emit(fieldTypeEmitName, value);
			}
		},
		buildFieldKey(param) {
			const { params_map } = this.dynamicNode || this.widgetFieldSource;
			return ParamsService.buildFieldKey(param, params_map);
		},
		async saveFieldValueToStore(fieldName, fieldValue) {
			const currentValue = { ...(this[configurationsValuesByFieldUUID.fullPath][this.interfaceFieldUUID] || {}) };
			currentValue[fieldName] = fieldValue;
			await this[setMappingConfigurationFieldValues.fullPath]({
				interfaceUUID: this.interface.id,
				fieldUUID: this.interfaceFieldUUID,
				fieldValue: currentValue,
			});
		},
	},
	mounted() {
		this.localWidgetFieldSource = this.widgetFieldSource;
		if (this.dynamicNode) {
			this.$set(this, 'localResponseData', {
				params_map: this.dynamicNode.params_map,
				params_map_labels: this.dynamicNode.params_map_labels,
			});
		}
	},
	watch: {
		dynamicNode() {
			this.$set(this, 'localResponseData', {
				params_map: this.dynamicNode.params_map,
				params_map_labels: this.dynamicNode.params_map_labels,
			});
		},
	},
	data() {
		return {
			allPossibleFields: [],
			localWidgetFieldSource: {},
			localResponseData: {},
		};
	},
};
</script>

<style lang="scss" scoped>
.dynamic-params-dispatcher {
}
</style>
